import { useParams, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Scan, 
  AlertCircle, 
  FileText,
  ArrowLeft,
  CheckCircle2,
  Zap,
  Users,
  TrendingUp,
  Shield,
  Clock,
  Microscope,
  BrainCircuit
} from 'lucide-react';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { Button } from '../../components/ui/button';

// --- UPDATED DATA TO MATCH NEW BRANDING ---

const autoScanData = {
  id: 'auto-scan',
  icon: Scan,
  title: 'Auto Scan & Evaluation',
  tagline: 'Turn your phone into a strict Board Examiner.',
  description: 'Most AI graders just verify the final answer. Edalyze performs a step-by-step forensic audit of your solution, applying official board marking schemes to catch errors before the examiner does.',
  iconColor: 'text-blue-500',
  bgGradient: 'from-blue-500/10 to-blue-600/5'
};

const errorClassData = {
  id: 'error-classification',
  icon: AlertCircle,
  title: 'Error Classification',
  tagline: 'Don\'t just see the "X". See the "Why".',
  description: 'We move beyond "Right or Wrong." Our engine distinguishes between a Conceptual Gap (needs study), a Careless Error (needs focus), or a Strategic Flaw (needs time management).',
  iconColor: 'text-red-500',
  bgGradient: 'from-red-500/10 to-red-600/5'
};

const examPaperData = {
  id: 'exam-paper-generation',
  icon: FileText,
  title: 'Targeted Paper Gen', // Renamed for impact
  tagline: 'Never make the same mistake twice.',
  description: 'Generic practice is inefficient. Edalyze generates custom question papers that specifically target your weak zones, forcing you to master the concepts you struggle with.',
  iconColor: 'text-primary',
  bgGradient: 'from-primary/10 to-primary/5'
};

const capabilityData = {
  'auto-scan': autoScanData,
  'error-classification': errorClassData,
  'exam-paper-generation': examPaperData
};

// --- UPDATED FEATURES (NO FAKE STATS) ---

const autoScanFeatures = [
  { icon: Zap, title: 'Step-Wise Marking', description: 'We don\'t just grade the final output. We evaluate the logic, steps, and keywords used in the derivation.' },
  { icon: Shield, title: 'Board-Sync™ Logic', description: 'Calibrated to match the strict marking schemes of CBSE and ICSE. If the board cuts marks for it, so do we.' },
  { icon: CheckCircle2, title: 'Handwriting Agnostic', description: 'Advanced OCR reads messy student handwriting with high precision, just like a human teacher.' },
  { icon: Clock, title: 'Instant Feedback Loop', description: 'Scan a paper at 11 PM, get a detailed analysis by 11:02 PM. No waiting for school tutors.' }
];

const errorClassFeatures = [
  { icon: Microscope, title: 'Root Cause Analysis', description: 'Identifies if you lost marks because you didn\'t know the Physics, or because your Math integration was weak.' },
  { icon: TrendingUp, title: 'Silly Mistake Tracker', description: 'Quantifies exactly how many marks you lose to "Careless Errors" vs "Genuine Knowledge Gaps".' },
  { icon: BrainCircuit, title: 'Concept Mapping', description: 'Links every specific error back to the chapter and sub-topic in your syllabus.' },
  { icon: Shield, title: 'Blind Spot Detection', description: 'Highlights recurring mistakes you make across different chapters that you haven\'t noticed.' }
];

const examPaperFeatures = [
  { icon: CheckCircle2, title: 'Gap-Focused Questions', description: 'Generates questions specifically testing the topics you got wrong in previous tests.' },
  { icon: Zap, title: 'Smart Difficulty Curve', description: 'Questions start easy to build confidence, then ramp up to Board/JEE level to test mastery.' },
  { icon: TrendingUp, title: 'Infinite Re-Takes', description: 'Failed a topic? Generate 5 more papers on just that topic until you master it.' },
  { icon: Users, title: 'Marking Scheme Included', description: 'Every generated paper comes with a detailed answer key and marking logic.' }
];

export default function CapabilityDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const capability = capabilityData[id];

  // Scroll to top when component mounts or id changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [id]);

  if (!capability) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Capability not found</h1>
          <Button onClick={() => navigate('/')}>Back to Home</Button>
        </div>
      </div>
    );
  }

  const Icon = capability.icon;
   
  // Get features based on capability ID
  const features = id === 'auto-scan' ? autoScanFeatures : 
                   id === 'error-classification' ? errorClassFeatures : 
                   examPaperFeatures;
   
  // --- UPDATED STEPS FOR CLARITY ---
  const steps = id === 'auto-scan' ? [
    { step: '1', title: 'Scan & Upload', description: 'Snap a photo of your handwritten answer sheet (or upload a PDF).' },
    { step: '2', title: 'Forensic Analysis', description: 'Our engine maps your answers against the official board marking scheme.' },
    { step: '3', title: 'The Verdict', description: 'See your score, step-wise deductions, and exactly where you deviated.' }
  ] : id === 'error-classification' ? [
    { step: '1', title: 'Pattern Recognition', description: 'Edalyze scans your past tests to find recurring error patterns.' },
    { step: '2', title: 'Categorization', description: 'We tag errors as Conceptual, Strategic, or Careless.' },
    { step: '3', title: 'Strategy Fix', description: 'Get a personalized plan to fix the specific habits costing you marks.' }
  ] : [
    { step: '1', title: 'Select Weakness', description: 'Choose the specific chapters or concepts you are struggling with.' },
    { step: '2', title: 'Generate Paper', description: 'AI creates a unique question paper targeted at those specific gaps.' },
    { step: '3', title: 'Solve & Verify', description: 'Solve it, scan it, and verify if the gap is closed.' }
  ];
   
  // --- UPDATED BENEFITS (HIGH VALUE) ---
  const benefits = id === 'auto-scan' ? [
    'Stop guessing why you lost marks',
    'Get step-wise feedback instantly',
    'Verify syllabus coverage in real-time',
    'Works with CBSE, ICSE & State Boards',
    'Detects skipped steps in derivations',
    'Zero-hallucination grading logic'
  ] : id === 'error-classification' ? [
    'Distinguish "Silly" from "Serious" errors',
    'Stop losing marks to calculation mistakes',
    'Fix hidden prerequisites (e.g. Math in Physics)',
    'Improve exam temperament',
    'Visualize your error trends over time',
    'Prioritize revision based on data'
  ] : [
    'Never run out of practice questions',
    'Stop wasting time on easy questions',
    'Master specific weak topics',
    'Get used to Board-level difficulty',
    'Instant answer keys for everything',
    'Save money on generic test series'
  ];
   
  // --- UPDATED USE CASES (SPECIFIC) ---
  const useCases = id === 'auto-scan' ? [
    { title: 'The Anxious Student', description: 'Who needs to know exactly where they stand before the real exam.' },
    { title: 'The Busy Parent', description: 'Who wants to audit their child\'s progress without nagging them.' },
    { title: 'The Last-Minute Reviser', description: 'Who needs instant feedback on a practice paper at 2 AM.' }
  ] : id === 'error-classification' ? [
    { title: 'The "Silly Mistake" Victim', description: 'Students who know the concept but keep losing marks to calculation errors.' },
    { title: 'The Plateaued Topper', description: 'Students stuck at 90% who need to find that hidden 10% gap.' },
    { title: 'The Competitive Aspirant', description: 'JEE/NEET students needing to optimize exam strategy.' }
  ] : [
    { title: 'Targeted Improvement', description: 'For students who need to drill down into one specific weak chapter.' },
    { title: 'Exam Simulation', description: 'Create full-length mock tests to build stamina and time management.' },
    { title: 'Daily Drill', description: 'Generate short 20-minute quizzes to keep concepts fresh.' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
       
      <main className="pt-24">
        {/* Hero Section */}
        <section className={`relative py-20 bg-gradient-to-br ${capability.bgGradient} overflow-hidden`}>
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            {/* Back Button */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="mb-8"
            >
              <Button
                variant="ghost"
                onClick={() => navigate('/')}
                className="gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Home
              </Button>
            </motion.div>

            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Content */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-card border border-border mb-6">
                  <Icon className={`w-5 h-5 ${capability.iconColor}`} />
                  <span className="text-sm font-medium">Core Capability</span>
                </div>
                
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
                  {capability.title}
                </h1>
                
                <p className="text-xl md:text-2xl text-muted-foreground mb-8">
                  {capability.tagline}
                </p>
                
                <p className="text-lg text-muted-foreground leading-relaxed">
                  {capability.description}
                </p>
              </motion.div>

              {/* Icon Display */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
                className="flex items-center justify-center"
              >
                <div className={`relative w-80 h-80 rounded-3xl bg-gradient-to-br ${capability.bgGradient} flex items-center justify-center`}>
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                    className="absolute inset-0 opacity-20"
                    style={{
                      backgroundImage: 'radial-gradient(circle at 30% 50%, rgba(99, 179, 237, 0.3) 0%, transparent 50%)'
                    }}
                  />
                  <Icon className={`w-40 h-40 ${capability.iconColor}`} />
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Key Features */}
        <section className="py-20 bg-background">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Key Features</h2>
              <p className="text-lg text-muted-foreground">
                Engineered for precision and speed
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8">
              {features.map((feature, index) => {
                const FeatureIcon = feature.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex gap-4 p-6 rounded-xl bg-card border border-border hover:border-primary/30 transition-all"
                  >
                    <div className={`p-3 rounded-lg bg-primary/10 h-fit`}>
                      <FeatureIcon className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                      <p className="text-muted-foreground">{feature.description}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-20 bg-muted/50">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-4">How It Works</h2>
              <p className="text-lg text-muted-foreground">
                Simple process, forensic results
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-8">
              {steps.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.15 }}
                  className="relative"
                >
                  {/* Connector Line */}
                  {index < steps.length - 1 && (
                    <div className="hidden md:block absolute top-12 left-full w-full h-0.5 bg-border -translate-x-4" />
                  )}
                   
                  <div className="relative bg-card border border-border rounded-xl p-6 text-center h-full">
                    <div className="w-16 h-16 rounded-full bg-primary/10 border-2 border-primary flex items-center justify-center mx-auto mb-4">
                      <span className="text-2xl font-bold text-primary">{step.step}</span>
                    </div>
                    <h3 className="text-xl font-semibold mb-3">{step.title}</h3>
                    <p className="text-muted-foreground">{step.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="py-20 bg-background">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <h2 className="text-3xl md:text-4xl font-bold mb-6">Why Students Choose Edalyze</h2>
                <p className="text-lg text-muted-foreground mb-8">
                  Stop settling for "Work Harder." Start working smarter.
                </p>
                
                <div className="space-y-4">
                  {benefits.map((benefit, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.05 }}
                      className="flex items-start gap-3"
                    >
                      <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-lg">{benefit}</span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="aspect-square rounded-2xl bg-gradient-to-br from-primary/10 to-primary/5 p-12 flex items-center justify-center">
                  <Icon className={`w-full h-full ${capability.iconColor} opacity-20`} />
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Use Cases */}
        <section className="py-20 bg-muted/50">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Who Is This For?</h2>
              <p className="text-lg text-muted-foreground">
                Designed for students who want every mark they deserve.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-8">
              {useCases.map((useCase, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="p-8 rounded-xl bg-card border border-border hover:border-primary/30 transition-all"
                >
                  <Users className="w-12 h-12 text-primary mb-4" />
                  <h3 className="text-2xl font-semibold mb-4">{useCase.title}</h3>
                  <p className="text-muted-foreground text-lg">{useCase.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className={`py-20 bg-gradient-to-br ${capability.bgGradient}`}>
          <div className="max-w-4xl mx-auto px-6 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
                Start your diagnosis with {capability.title}.
              </h2>
              <p className="text-xl text-muted-foreground">
                Get the app and scan your first paper for free.
              </p>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
